import { useState } from "react";
import { KeyRound, Plus, Lock, ArrowLeft, ShieldAlert, MoreVertical, Copy, Pencil, Trash2, FolderKanban, Server } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { SECRET_MASK, SECRET_ENVIRONMENTS } from "@/mocks/secrets";
import { PageHeader, DataTable, DemoBadge, AsyncBoundary, CardSkeleton, RowsSkeleton, ProjectAvatar, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { demoAction } from "@/lib/demo";

const secretActions = (name) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild><button data-testid={`secret-actions-${name}`} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/5 hover:text-foreground"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="border-line bg-popover">
      <DropdownMenuItem onClick={() => demoAction("Copiar referência")}><Copy className="mr-2 h-4 w-4" />Copiar referência</DropdownMenuItem>
      <DropdownMenuItem onClick={() => demoAction("Editar secret")}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
      <DropdownMenuItem onClick={() => demoAction("Rotacionar secret")}><KeyRound className="mr-2 h-4 w-4" />Rotacionar</DropdownMenuItem>
      <DropdownMenuItem onClick={() => demoAction("Remover secret")} className="text-err focus:text-err"><Trash2 className="mr-2 h-4 w-4" />Remover</DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const NewSecretDialog = ({ open, onOpenChange, defaultEnv = "Production" }) => {
  const [env, setEnv] = useState(defaultEnv);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-line bg-popover">
        <DialogHeader>
          <DialogTitle className="font-heading">Novo Secret</DialogTitle>
          <DialogDescription>O valor é armazenado com segurança e nunca exibido na listagem.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5"><Label>Nome</Label><Input placeholder="DATABASE_URL" className="border-line bg-surface font-mono" data-testid="secret-name-input" /></div>
          <div className="space-y-1.5"><Label>Environment</Label>
            <Select value={env} onValueChange={setEnv}>
              <SelectTrigger className="border-line bg-surface" data-testid="secret-env-input"><SelectValue /></SelectTrigger>
              <SelectContent className="border-line bg-popover">{SECRET_ENVIRONMENTS.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Valor</Label><Input type="password" placeholder="••••••••" className="border-line bg-surface font-mono" data-testid="secret-value-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-line bg-transparent hover:bg-white/5">Cancelar</Button>
          <Button onClick={() => { onOpenChange(false); demoAction("Secret criado"); }} className="bg-brand text-white hover:bg-brand-hover" data-testid="secret-save">Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ProjectSecretsView = ({ project, onBack }) => {
  const { data, loading, error } = useAsync(() => api.getProjectSecrets(project.id), [project.id]);
  const [newOpen, setNewOpen] = useState(false);
  return (
    <div className="space-y-5">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="vault-back">
        <ArrowLeft className="h-4 w-4" /> Todos os projetos
      </button>
      <div className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <ProjectAvatar name={project.name} />
          <div><p className="font-heading text-lg font-semibold text-foreground">{project.name}</p><p className="text-xs text-muted-foreground">{project.client} · {project.count} secrets</p></div>
        </div>
        <Button onClick={() => setNewOpen(true)} className="bg-brand text-white hover:bg-brand-hover" data-testid="vault-new-secret"><Plus className="h-4 w-4" /> Novo Secret</Button>
      </div>

      <AsyncBoundary loading={loading} error={error} skeleton={<RowsSkeleton />}>
        {data && (
          <Tabs defaultValue="Production">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-line bg-surface p-1">
              {SECRET_ENVIRONMENTS.map((e) => (
                <TabsTrigger key={e} value={e} data-testid={`vault-env-${e}`} className="data-[state=active]:bg-brand data-[state=active]:text-white">
                  {e} <span className="ml-1.5 rounded-full bg-white/10 px-1.5 text-[10px]">{data[e]?.length || 0}</span>
                </TabsTrigger>
              ))}
            </TabsList>
            {SECRET_ENVIRONMENTS.map((e) => (
              <TabsContent key={e} value={e} className="mt-5">
                <DataTable
                  columns={[
                    { key: "name", header: "Nome", render: (r) => <span className="inline-flex items-center gap-2 font-mono text-sm font-medium text-foreground"><Lock className="h-3.5 w-3.5 text-muted-foreground" />{r.name}</span> },
                    { key: "value", header: "Valor", render: () => <span className="font-mono text-xs text-muted-foreground">{SECRET_MASK}</span> },
                    { key: "env", header: "Environment", render: () => <span className="rounded border border-line px-2 py-0.5 text-xs text-muted-foreground">{e}</span> },
                    { key: "versions", header: "Versões", align: "center" },
                    { key: "updated", header: "Atualizado" },
                    { key: "actions", header: "", align: "right", render: (r) => secretActions(r.name) },
                  ]}
                  rows={data[e]}
                  empty={<EmptyState icon={KeyRound} title={`Sem secrets em ${e}`} description="Adicione um secret para este environment." action={{ label: "Novo Secret", onClick: () => setNewOpen(true) }} />}
                  testid={`vault-table-${e}`} />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </AsyncBoundary>
      <NewSecretDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
};

export default function VaultSecrets() {
  const { data: projects, loading, error } = useAsync(() => api.getVaultProjects());
  const { data: platform } = useAsync(() => api.getPlatformSecrets());
  const [selected, setSelected] = useState(null);

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader icon={KeyRound} title="Vault & Secrets" description="Segredos organizados por Projeto e Environment. Credenciais de infraestrutura ficam em Platform Secrets."
        breadcrumb={<><span>Organização</span><span>/</span><span className="text-foreground">Vault & Secrets</span></>}
        actions={<DemoBadge className="hidden sm:inline-flex" />} />

      <div className="flex items-center gap-3 rounded-xl border border-info/20 bg-info/5 p-3 text-sm text-info">
        <ShieldAlert className="h-4 w-4 shrink-0" />
        Os valores dos segredos são mascarados por segurança e nunca são exibidos na interface.
      </div>

      {selected ? (
        <ProjectSecretsView project={selected} onBack={() => setSelected(null)} />
      ) : (
        <Tabs defaultValue="projects">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-line bg-surface p-1">
            <TabsTrigger value="projects" data-testid="vault-tab-projects" className="data-[state=active]:bg-brand data-[state=active]:text-white"><FolderKanban className="mr-1.5 h-4 w-4" /> Secrets por Projeto</TabsTrigger>
            <TabsTrigger value="platform" data-testid="vault-tab-platform" className="data-[state=active]:bg-brand data-[state=active]:text-white"><Server className="mr-1.5 h-4 w-4" /> Platform Secrets</TabsTrigger>
          </TabsList>

          <TabsContent value="projects" className="mt-5">
            <AsyncBoundary loading={loading} error={error} skeleton={<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}</div>}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {projects?.map((p) => (
                  <button key={p.id} onClick={() => setSelected(p)} data-testid={`vault-project-${p.id}`} className="group flex items-center gap-3 rounded-xl border border-line bg-surface p-4 text-left transition-all hover:-translate-y-0.5 hover:bg-surface-hover">
                    <ProjectAvatar name={p.name} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-heading font-semibold text-foreground">{p.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{p.client}</p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-line bg-white/[0.03] px-2.5 py-1 text-xs text-muted-foreground"><KeyRound className="h-3 w-3" />{p.count}</span>
                  </button>
                ))}
              </div>
            </AsyncBoundary>
          </TabsContent>

          <TabsContent value="platform" className="mt-5 space-y-3">
            <p className="text-sm text-muted-foreground">Credenciais da própria infraestrutura TintiCloud — separadas dos secrets de aplicações/clientes.</p>
            <DataTable
              columns={[
                { key: "name", header: "Nome", render: (r) => <span className="inline-flex items-center gap-2 font-mono text-sm font-medium text-foreground"><Lock className="h-3.5 w-3.5 text-brand/70" />{r.name}</span> },
                { key: "value", header: "Valor", render: () => <span className="font-mono text-xs text-muted-foreground">{SECRET_MASK}</span> },
                { key: "category", header: "Categoria", render: (r) => <span className="rounded border border-line px-2 py-0.5 text-xs text-muted-foreground">{r.category}</span> },
                { key: "versions", header: "Versões", align: "center" },
                { key: "updated", header: "Atualizado" },
                { key: "actions", header: "", align: "right", render: (r) => secretActions(r.name) },
              ]}
              rows={platform} testid="platform-secrets-table" />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
